import React, { useState, useCallback, useEffect } from 'react';
import type { GetItemsFilters, MasterDataForComponents } from '../../../types';
import { CaltivationCategoryOptions, ProductionHistoryOptions, statusOptions } from '../../../constants';
import { useItemFilters } from '../../../contexts/ItemFilterContext';
import { formatToDateInput, formatFromDateInput } from '../../../utils/date';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Search } from 'lucide-react';
import { MasterSearchDialog } from '../../common/MasterSearch/MasterSearchDialog';
import { useCodeName } from '../../../hooks/useUserMasterCodeName';
import { MasterCategoryConst, type MasterCategoryType } from '../../../types/masterSearchTypes';

dayjs.extend(utc);
dayjs.extend(timezone);
// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

//----------------
// Props
//----------------
interface BiddingItemMaintenanceFilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  masterData: MasterDataForComponents;
  isSeller: boolean;
  isFarmer: boolean;
}

// ソート可能な項目の定義
const sortOptions = [
  { value: '', label: '指定なし' },
  { value: 'ReceptionDate', label: '受付日' },
  { value: 'BiddingDate', label: '入札日' },
  { value: 'BiddingNumber', label: '入札No.' },
  { value: 'SellerCode', label: '売手' },
  { value: 'FarmerCode', label: '生産者' },
  { value: 'Brand', label: '荷印' },
  { value: 'TeaSeasonCode', label: '茶期' },
  { value: 'TeaTypeCode', label: '茶種' },
  { value: 'VarietyCode', label: '品種名' },
  { value: 'GapCode', label: 'GAP区分' },
  { value: 'CaltivationCategory', label: '栽培区分' },
  { value: 'ProductionHistory', label: '生産履歴' },
  { value: 'Status', label: 'ステータス' },
];

//--------------------------------
// 出品管理の検索条件指定ダイアログコンポーネント
//--------------------------------
export const BiddingItemMaintenanceFilterDialog = ({ 
  isOpen, onClose, masterData, isSeller, isFarmer
}: BiddingItemMaintenanceFilterDialogProps) => {

  //----------------
  // Context使用定義
  //----------------

  // フィルターcontextより検索条件を取得
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();

  //----------------
  // State定義
  //----------------

  const [localFilters, setLocalFilters] = useState<GetItemsFilters>(() => {
    const initial = { ...itemFilters };
    if (initial.gapCode === undefined) {
      initial.gapCode = ''; 
    }
    return initial;
  });

  // カスタムフック：名称表示用の codes/names 管理
  const { codes, setCodes, names } = useCodeName();

  // 検索ダイアログ制御用の状態
  const [isSearchOpen, setIsSearchOpen] = useState(false); // ダイアログの開閉
  const [searchCategory, setSearchCategory] = useState<MasterCategoryType>(MasterCategoryConst.SELLER); // 検索対象マスタ
  const [activeTarget, setActiveTarget] = useState<'seller' | 'farmer'>('seller'); // どの入力欄に対する検索か

  //----------------
  // Effect定義
  //----------------

  // ダイアログが開いた時、またはフィルター値が変わった時にフック側の状態を同期
  useEffect(() => {
    if (isOpen) {
      setCodes({
        seller: localFilters.sellerCode || null,
        farmer: localFilters.farmerCode || null,
        buyer: null,
      });
    }
  }, [isOpen, localFilters.sellerCode, localFilters.farmerCode, setCodes]);

  //----------------
  // イベントハンドラー定義
  //----------------

  // フォーム入力ハンドラ
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    let processedValue: string | number;
    let sanitizedValue = value;

    if (type === 'date') {
      processedValue = formatFromDateInput(value);
    } else {
      const isNumeric = ['sellerCode', 'farmerCode', 'teaSeasonCode', 'teaTypeCode', 'varietyCode', 'gapCode', 'caltivationCategory', 'productionHistory', 'listingCategory', 'status'].includes(name);
      if (isNumeric) {
        sanitizedValue = value.replace(/[^0-9]/g, '');
        processedValue = (sanitizedValue === '' ? '' : Number(sanitizedValue));
      } else {
        processedValue = value;
      }
    }

    setLocalFilters(prev => {
      const newFilters = { ...prev, [name]: processedValue };
      
      // 売手が変更されたら生産者を空にし、GAP区分は「すべて」にする
      if (name === 'sellerCode') {
        const numVal = sanitizedValue === '' ? null : Number(sanitizedValue);
        setCodes(p => ({ ...p, seller: numVal, farmer: null, buyer: null })); // フック側同期
        newFilters.farmerCode = ''; 
        newFilters.gapCode = ''; 
      }
      
      // 生産者が変更された時の連動
      if (name === 'farmerCode') {
        const numVal = sanitizedValue === '' ? null : Number(sanitizedValue);
        setCodes(p => ({ ...p, farmer: numVal, buyer: null })); // フック側同期
        if (processedValue === '') {
          newFilters.gapCode = '';
        } else {
          const selectedFarmer = masterData.farmers.find(
            f => String(f.sellerCode) === String(newFilters.sellerCode) && 
                 String(f.farmerCode) === String(processedValue)
          );

          if (selectedFarmer && selectedFarmer.gapCode != null) {
            // 基幹の0が来たらに「すべて」に翻訳する
            newFilters.gapCode = selectedFarmer.gapCode === 0 ? '' : selectedFarmer.gapCode;
          } else {
            newFilters.gapCode = ''; 
          }
        }
      }

      return newFilters;
    });
  }, [masterData.farmers, setCodes]);

  // ------------------------------------------------
  // ソートキー変更ハンドラ
  // ------------------------------------------------
  const handleSortChange = useCallback((index: number, value: string) => {
    setLocalFilters(prev => {
      const currentSorts = [...(prev.sortKeys || [])];
      
      if (value) {
        currentSorts[index] = value;
      } else {
        currentSorts.splice(index, 1);
      }

      return { ...prev, sortKeys: currentSorts };
    });
  }, []);

  const getSortKey = (index: number) => {
    return localFilters.sortKeys?.[index] || '';
  };

  const todayJST = dayjs().tz().format('YYYYMMDD');

  // 一括クリアボタンハンドラ
  const handleClear = useCallback(() => {
    // フック側のコードをクリア
    setCodes({ 
      seller: (isSeller || isFarmer) ? localFilters.sellerCode || null : null, 
      farmer: isFarmer ? localFilters.farmerCode || null : null,
      buyer: null
    });

    setLocalFilters(prev => ({
      ...prev,              
      receptionDateFrom: '',
      receptionDateTo: '',
      sellerCode: (isSeller || isFarmer) ? prev.sellerCode : '',
      farmerCode: isFarmer ? prev.farmerCode : '',
      brand: '',
      teaSeasonCode: '',
      teaTypeCode: '',
      varietyCode: '',
      gapCode: '',           
      caltivationCategory: '',
      productionHistory: '',
      status: '',          
      sortKeys: []         
    }));
  }, [todayJST, isSeller, isFarmer, localFilters.sellerCode, localFilters.farmerCode, setCodes]);

  const handleApply = useCallback(() => {
    updateFiltersAndNavigate(localFilters);
    onClose();
  }, [localFilters, updateFiltersAndNavigate, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  /** マスタ検索ダイアログを表示する */
  const openSearch = (cat: MasterCategoryType, target: 'seller' | 'farmer') => {
    // 生産者検索は、親となる売手コードが入力されている場合のみ許可
    if (target === 'farmer' && !localFilters.sellerCode) return;

    setSearchCategory(cat);
    setActiveTarget(target);
    setIsSearchOpen(true);
  };

  /** ダイアログでマスタを選択した際の処理 */
  const handleSelectMaster = (selectedData: any) => {
    if (activeTarget === 'seller') {
      const code = selectedData.sellerCode ?? null;
      setCodes(p => ({ ...p, seller: code, farmer: null, buyer: null }));
      setLocalFilters(p => ({ 
        ...p, 
        sellerCode: code ?? "", 
        farmerCode: "", 
        gapCode: "" 
      }));
      setTimeout(() => {
        const nextEl = document.getElementsByName('farmerCode')[0] as HTMLElement;
        nextEl?.focus();
      }, 100);
    } else if (activeTarget === 'farmer') {
      const fCode = selectedData.farmerCode ?? null;
      setCodes(p => ({ ...p, farmer: fCode, buyer: null }));
      setLocalFilters(p => ({ 
        ...p, 
        farmerCode: fCode ?? "", 
        gapCode: selectedData.gapCode ?? ""
      }));
      setTimeout(() => {
        const nextEl = document.getElementsByName('brand')[0] as HTMLElement;
        nextEl?.focus();
      }, 100);        
    }
    setIsSearchOpen(false);
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter") {
      // 備考欄などでEnterを押したときにフォーム送信されるのを防ぐ
      e.preventDefault();

      const container = e.currentTarget.closest("div.bg-white") || document;
      // フォーカス可能な要素をすべて取得
      const focusableElements = Array.from(
        container.querySelectorAll('input:not([disabled]), select:not([disabled]), button:not([disabled])')
      ) as HTMLElement[];

      let index = focusableElements.indexOf(e.currentTarget);

      if (index > -1) {
        let nextIndex = index + 1;

        while (nextIndex < focusableElements.length) {
          const nextElement = focusableElements[nextIndex] as HTMLInputElement;
          
          const isCheckbox = nextElement.type === 'checkbox';
          const isRadio = nextElement.type === 'radio';
          // 検索ボタン（アイコンボタン）はスキップしたいが、保存ボタン（テキストがあるボタン）は止めたい
          // もしくは「bg-indigo-600」などのクラス名で判定する
          const isSearchButton = nextElement.tagName === 'BUTTON' && !nextElement.innerText.includes("検索条件を適用");
          const isDisabled = (nextElement as any).disabled;

          if (isCheckbox || isRadio || isSearchButton || isDisabled) {
            nextIndex++;
          } else {
            // 保存ボタン、または次の入力項目が見つかったらフォーカス
            nextElement.focus();
            break;
          }
        }
      }
    }
  }, []);
  
  //----------------
  // その他定義
  //----------------

  const inputClasses = "w-full p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500 focus:border-indigo-500 h-[42px] appearance-none safari-date-fix";
  const labelClasses = "block text-sm font-medium text-slate-700";
  
  // マスタコード入力欄専用スタイル
  const codeInputClasses = "mt-1 w-16 p-2 border border-slate-300 rounded-l-md bg-white focus:ring-1 focus:ring-indigo-500 text-sm h-[42px] outline-none shadow-sm disabled:bg-slate-200 disabled:cursor-not-allowed text-left";
  // 名称表示エリア（入力不可スタイル）
  const nameDisplayClasses = "ml-1 mt-1 flex-1 text-sm text-slate-600 truncate font-medium bg-slate-200 border border-slate-300 rounded-md px-2 h-[42px] flex items-center";

  //----------------
  // レンダリング処理
  //----------------

  if (!isOpen) return null; 

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm bg-opacity-50" onClick={handleCancel}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full flex flex-col max-h-[90vh] md:max-h-[85vh]" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold mb-0">検索条件</h2>
        </div>

        <div className="p-4 md:p-6 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="form-group"><label className={labelClasses}>受付日 (From)</label>
              <input 
                type="date" name="receptionDateFrom" value={formatToDateInput(String(localFilters.receptionDateFrom))} 
                onKeyDown={handleKeyDown}
                onChange={handleChange} className={inputClasses}/></div>
            <div className="form-group"><label className={labelClasses}>受付日 (To)</label>
              <input type="date" name="receptionDateTo" value={formatToDateInput(String(localFilters.receptionDateTo))} 
                onKeyDown={handleKeyDown}
                onChange={handleChange} className={inputClasses}/></div>
            <div className="hidden lg:block" />

            {/* 売手・生産者：PC(lg)で確実に1行（100%）を使い、それぞれ50%ずつに分ける */}
            <div className="md:col-span-2 lg:col-span-3 flex flex-col md:flex-row gap-4 md:gap-6">
              {/* 売手 */}
              <div className="md:w-1/2">
                <label className={labelClasses}>売手</label>
                <div className="flex items-center gap-0">
                  <input
                    type="text"
                    name="sellerCode"
                    value={codes.seller ?? ''}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    disabled={isSeller || isFarmer}
                    className={codeInputClasses}
                  />
                  <button 
                    type="button"
                    disabled={isSeller || isFarmer}
                    onClick={() => openSearch(MasterCategoryConst.SELLER, 'seller')} 
                    className="mt-1 px-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-[42px] flex items-center justify-center transition-colors disabled:bg-slate-200"
                  >
                    <Search size={16} className="text-slate-500" />
                  </button>
                  <div className={nameDisplayClasses}>
                    {names.seller}
                  </div>
                </div>
              </div>

              {/* 生産者 */}
              <div className="md:w-1/2">
                <label className={labelClasses}>生産者</label>
                <div className="flex items-center gap-0">
                  <input
                    type="text"
                    name="farmerCode"
                    value={codes.farmer ?? ''}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    disabled={isFarmer || !localFilters.sellerCode}
                    className={codeInputClasses}
                  />
                  <button 
                    type="button"
                    disabled={isFarmer || !localFilters.sellerCode}
                    onClick={() => openSearch(MasterCategoryConst.FARMER, 'farmer')} 
                    className="mt-1 px-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-[42px] flex items-center justify-center transition-colors disabled:bg-slate-200"
                  >
                    <Search size={16} className="text-slate-500" />
                  </button>
                  <div className={nameDisplayClasses}>
                    {names.farmer}
                  </div>
                </div>
              </div>
            </div>

            {/* ここから荷印。親のグリッド（lg:grid-cols-3）に戻るため、自動的に次の行へ送られます */}
            <div className="form-group lg:col-span-1"><label className={labelClasses}>荷印</label><input type="text" name="brand" value={localFilters.brand} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}/></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>茶期</label><select name="teaSeasonCode" value={localFilters.teaSeasonCode} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{masterData.teaSeasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>茶種</label><select name="teaTypeCode" value={localFilters.teaTypeCode} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{masterData.teaTypes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>品種名</label><select name="varietyCode" value={localFilters.varietyCode} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{masterData.varieties.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
            <div className="form-group lg:col-span-1">
              <label className={labelClasses}>GAP区分</label>
              <select 
                name="gapCode" 
                // 値が undefined や null の場合は空文字（すべて）として扱う
                value={localFilters.gapCode ?? ''} 
                onKeyDown={handleKeyDown}
                onChange={handleChange} 
                className={inputClasses}
              >
                <option value="">すべて</option>
                <option value={9}>GAPなし</option>
                {masterData.gaps
                  .filter(s => Number(s.gapCode) !== 0 && Number(s.gapCode) !== 9)
                  .map(s => (
                    <option key={s.gapCode} value={s.gapCode}>
                      {s.gapName}
                    </option>
                  ))}
              </select>
            </div>      
            
            <div className="form-group lg:col-span-1"><label className={labelClasses}>栽培区分</label><select name="caltivationCategory" value={localFilters.caltivationCategory} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{CaltivationCategoryOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>生産履歴</label><select name="productionHistory" value={localFilters.productionHistory} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{ProductionHistoryOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select></div>
            <div className="form-group lg:col-span-1"><label className={labelClasses}>ステータス</label><select name="status" value={localFilters.status} 
              onKeyDown={handleKeyDown}
              onChange={handleChange} className={inputClasses}><option value="">すべて</option>{statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select></div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-slate-700 mb-4 mt-6">並び替え順 (最大3つ)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="form-group">
                <label className={labelClasses}>優先順位 1</label>
                <select value={getSortKey(0)} 
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleSortChange(0, e.target.value)} className={inputClasses}>
                  {sortOptions.map(opt => <option key={`sort1-${opt.value}`} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className={labelClasses}>優先順位 2</label>
                <select value={getSortKey(1)} 
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleSortChange(1, e.target.value)} className={inputClasses} disabled={!getSortKey(0)}>
                  {sortOptions.map(opt => <option key={`sort2-${opt.value}`} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className={labelClasses}>優先順位 3</label>
                <select value={getSortKey(2)} 
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleSortChange(2, e.target.value)} className={inputClasses} disabled={!getSortKey(1)}>
                  {sortOptions.map(opt => <option key={`sort3-${opt.value}`} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-slate-200 sticky bottom-0 bg-white z-10">
          <div className="flex flex-col-reverse md:flex-row justify-between items-center gap-4">
            <button onClick={handleClear} className="w-full md:w-auto px-4 py-3 md:py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors border border-slate-200 md:border-transparent">
              条件を一括クリア
            </button>
            <div className="grid grid-cols-2 w-full md:w-auto md:flex gap-3 md:gap-4">
              <button onClick={handleCancel} className="w-full md:w-auto px-4 py-3 md:py-2 rounded-md bg-slate-200 hover:bg-slate-300 font-medium">
                キャンセル
              </button>
              <button onClick={handleApply} className="w-full md:w-auto px-4 py-3 md:py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 font-semibold shadow-sm">
                検索条件を適用
              </button>
            </div>
          </div>
        </div>
      </div>

      <MasterSearchDialog 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
        category={searchCategory} 
        initialValues={{ sellerCode: localFilters.sellerCode, sellerName: names.seller }} 
        onSelect={handleSelectMaster} 
      />
    </div>
  );
};